import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisitorHistory = () => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [areaFilter, setAreaFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVisitors, setSelectedVisitors] = useState([]);
  const { user, logout } = useAuth();

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/visitors/history`);
      setVisitors(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredVisitors = visitors.filter(visitor => {
    const matchesStatus = filter === 'all' || visitor.status === filter;
    const matchesArea = areaFilter === 'all' || visitor.area === areaFilter;
    const matchesSearch = visitor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         visitor.created_by_user?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesArea && matchesSearch;
  });

  const stats = {
    total: visitors.length,
    pending: visitors.filter(v => v.status === 'pending').length,
    used: visitors.filter(v => v.status === 'used').length
  };

  const handleExportCSV = () => {
    const headers = ['Nombre', 'Tipo', 'Área', 'Placa', 'Marca', 'Color', 'Estado', 'Creado Por', 'Departamento', 'Fecha Creación', 'Fecha Escaneo'];
    
    const csvData = filteredVisitors.map(visitor => [
      visitor.name,
      visitor.visitor_type,
      visitor.area || '',
      visitor.vehicle_plate || '',
      visitor.vehicle_brand || '',
      visitor.vehicle_color || '',
      visitor.status === 'pending' ? 'Pendiente' : 'Utilizado',
      visitor.created_by_user || 'N/A',
      visitor.created_by_department || 'N/A',
      formatDate(visitor.created_at),
      visitor.scanned_at ? formatDate(visitor.scanned_at) : '-'
    ]);

    const csv = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `historial_visitantes_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Historial exportado a CSV');
  };

  const handlePrintPDF = () => {
    window.print();
    toast.success('Preparando impresión PDF');
  };

  const handleDeleteSingle = async (visitorId, visitorName) => {
    if (!window.confirm(`¿Está seguro de eliminar al visitante "${visitorName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/visitors/${visitorId}`);
      toast.success('Visitante eliminado exitosamente');
      fetchHistory();
    } catch (error) {
      console.error('Error deleting visitor:', error);
      toast.error('Error al eliminar visitante');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedVisitors.length === 0) {
      toast.error('Seleccione al menos un visitante para eliminar');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar ${selectedVisitors.length} visitante(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedVisitors.map(id => axios.delete(`${API}/visitors/${id}`))
      );
      
      toast.success(`${selectedVisitors.length} visitante(s) eliminado(s)`);
      setSelectedVisitors([]);
      fetchHistory();
    } catch (error) {
      console.error('Error deleting visitors:', error);
      toast.error('Error al eliminar visitantes');
    }
  };

  const toggleSelectVisitor = (visitorId) => {
    setSelectedVisitors(prev => 
      prev.includes(visitorId) 
        ? prev.filter(id => id !== visitorId)
        : [...prev, visitorId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedVisitors.length === filteredVisitors.length) {
      setSelectedVisitors([]);
    } else {
      setSelectedVisitors(filteredVisitors.map(v => v.id));
    }
  };

  return (
    <div className="history-container" data-testid="visitor-history">
      <div className="history-header">
        <div className="header-left">
          <h1 data-testid="history-title">Historial de Visitantes</h1>
          <p className="user-info">Usuario: <strong>{user?.username}</strong> | Departamento: <strong>{user?.department}</strong></p>
        </div>
        <div className="header-actions">
          <Link to="/admin" className="back-button" data-testid="back-admin-btn">
            Panel Admin
          </Link>
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pendientes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.used}</div>
          <div className="stat-label">Utilizados</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="form-group">
          <label>Buscar</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre o usuario"
            data-testid="search-input"
          />
        </div>

        <div className="form-group">
          <label>Estado</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="status-filter"
          >
            <option value="all">Todos</option>
            <option value="pending">Pendientes</option>
            <option value="used">Utilizados</option>
          </select>
        </div>

        <div className="form-group">
          <label>Área</label>
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            data-testid="area-filter"
          >
            <option value="all">Todas</option>
            <option value="ventas">Ventas</option>
            <option value="compras">Compras</option>
            <option value="contraloría">Contraloría</option>
            <option value="dirección general">Dirección General</option>
            <option value="bodega">Bodega</option>
            <option value="seguridad">Seguridad</option>
          </select>
        </div>
      </div>

      {user?.role === 'admin' && (
        <div className="export-actions">
          <Link to="/security-registries" className="btn-export" data-testid="security-registries-btn">
            📋 Registros de Seguridad
          </Link>
          <button 
            className="btn-export"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            📊 Descargar CSV
          </button>
          <button 
            className="btn-export"
            onClick={handlePrintPDF}
            data-testid="print-pdf-btn"
          >
            📄 Imprimir PDF
          </button>
          {selectedVisitors.length > 0 && (
            <button 
              className="btn-delete-selected"
              onClick={handleDeleteSelected}
              data-testid="delete-selected-btn"
            >
              🗑️ Eliminar Seleccionados ({selectedVisitors.length})
            </button>
          )}
        </div>
      )}

      <div className="history-content">
        {loading ? (
          <div className="loading">Cargando historial...</div>
        ) : filteredVisitors.length === 0 ? (
          <div className="no-visitors">
            <div className="empty-state-icon">📋</div>
            <p>No se encontraron registros</p>
          </div>
        ) : (
          <div className="history-table">
            <table data-testid="history-table">
              <thead>
                <tr>
                  {user?.role === 'admin' && (
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedVisitors.length === filteredVisitors.length && filteredVisitors.length > 0}
                        onChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Área</th>
                  <th>Placa</th>
                  <th>Marca</th>
                  <th>Color</th>
                  <th>Estado</th>
                  <th>Creado Por</th>
                  <th>Departamento</th>
                  <th>Fecha Creación</th>
                  <th>Fecha Escaneo</th>
                  {user?.role === 'admin' && <th>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {filteredVisitors.map((visitor) => (
                  <tr key={visitor.id} data-testid={`history-row-${visitor.id}`}>
                    {user?.role === 'admin' && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedVisitors.includes(visitor.id)}
                          onChange={() => toggleSelectVisitor(visitor.id)}
                          data-testid={`select-checkbox-${visitor.id}`}
                        />
                      </td>
                    )}
                    <td className="visitor-name-cell">{visitor.name}</td>
                    <td>
                      <span className="visitor-type">{visitor.visitor_type}</span>
                    </td>
                    <td>
                      <span className="area-badge">{visitor.area}</span>
                    </td>
                    <td>{visitor.vehicle_plate || '-'}</td>
                    <td>{visitor.vehicle_brand || '-'}</td>
                    <td>{visitor.vehicle_color || '-'}</td>
                    <td>
                      <span className={`visitor-status ${
                        visitor.status === 'pending' ? 'status-pending' : 'status-used'
                      }`}>
                        {visitor.status === 'pending' ? 'Pendiente' : 'Utilizado'}
                      </span>
                    </td>
                    <td>{visitor.created_by_user || 'N/A'}</td>
                    <td>{visitor.created_by_department || 'N/A'}</td>
                    <td>{formatDate(visitor.created_at)}</td>
                    <td>{visitor.scanned_at ? formatDate(visitor.scanned_at) : '-'}</td>
                    {user?.role === 'admin' && (
                      <td>
                        <button
                          className="btn-delete-single"
                          onClick={() => handleDeleteSingle(visitor.id, visitor.name)}
                          data-testid={`delete-btn-${visitor.id}`}
                          title="Eliminar visitante"
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorHistory;